import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Organization, OrgPart, OrgHat } from '@newgenus/common';
import { Component, Inject, OnInit, OnDestroy, inject } from '@angular/core';
import { OrgViewToggleComponent, OrgListViewService } from '../../shared/org-view-toggle';
import { OrgEntityColorsService } from '../../../org-dashboard/services/org-entity-colors.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../../material.module';
import { OrgFilterBarComponent, OrgFilterConfig } from '../../shared/org-filter-bar/org-filter-bar.component';
import { OrgSearchBarComponent } from '../../shared/org-search-bar/org-search-bar.component';
import { fuzzyMatch } from '../../shared/org-filter-utils';
import { OrgBoardService } from '../../services/org-board-service';
import { Subject, takeUntil } from 'rxjs';
import { restrictToKeys } from '../../shared/only-keys';

export interface PositionsListDialogData {
    organization: Organization;
    /** Only these items (a staff member's dashboard tile, ISS-022); absent = all. */
    onlyKeys?: string[];
}

interface PositionDisplayCard {
    key: string;
    name: string;
    partType: string;
    resolvedType: string;
    depth: number;
    parentName: string;
    path: string;
    hatCount: number;
    userCount: number;
    childCount: number;
    sgCount: number;
    hatNames: string[];
    userNames: string[];
    colour: { background: string; color: string };
    orgPart: OrgPart;
}

/** Resolve part type: use partType if set, else infer from depth.
 *  Hierarchy: executive(1) → division(2) → department(3) → section(4) → unit(5) → post(6+) */
function resolvePartType(part: OrgPart): string {
    if (part.partType) return part.partType.toLowerCase();
    const depth = part.depth || 0;
    if (depth <= 1) return 'executive';
    if (depth === 2) return 'division';
    if (depth === 3) return 'department';
    if (depth === 4) return 'section';
    if (depth === 5) return 'unit';
    if (depth >= 6) return 'post';
    return '';
}

@Component({
    selector: 'shared-org-positions-list-dialog',
    imports: [
        MaterialModule,
        CommonModule,
        FormsModule,
        OrgFilterBarComponent,
        OrgSearchBarComponent,
        OrgViewToggleComponent,
    ],
    template: `
        <h2 mat-dialog-title class="dialog-header">
            <mat-icon class="mr-2 header-icon">business</mat-icon>
            Org structure parts ({{ filteredPositions.length }} of {{ allPositions.length }})

            <button
                mat-icon-button
                class="explorer-button"
                (click)="openExplorer()"
                matTooltip="Open Org Part Explorer - View hierarchical tree of org parts"
            >
                <mat-icon>account_tree</mat-icon>
            </button>

            <shared-org-view-toggle></shared-org-view-toggle>
            <button mat-icon-button mat-dialog-close class="close-button">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="dialog-content" [class.ng-list-view]="(listView.mode$ | async) === 'list'">
            <!-- Search Bar -->
            <shared-org-search-bar
                searchLabel="Search org parts"
                searchPlaceholder="Search by name, type, hat, staff member..."
                (searchChanged)="onSearchChanged($event)"
            >
            </shared-org-search-bar>

            <!-- Filters -->
            <shared-org-filter-bar
                [filters]="filterConfig"
                [rightFilters]="rightFilterConfig"
                activeColor="#388e3c"
                activeDarkColor="#66bb6a"
                [totalCount]="allPositions.length"
                (filtersChanged)="onFiltersChanged($event)"
            >
            </shared-org-filter-bar>

            <!-- Position card template (reused in both views) -->
            <ng-template #positionCardTpl let-position>
                <mat-card
                    class="position-card"
                    [class.post-card]="position.resolvedType === 'post'"
                    (dblclick)="onPositionDoubleClick(position)"
                >
                    <div class="card-identity">
                        <div
                            class="entity-avatar"
                            [ngStyle]="{
                                background:
                                    'linear-gradient(135deg, ' +
                                    entityColors.getColor('positions') +
                                    ' 0%, ' +
                                    entityColors.getColor('positions') +
                                    'cc 100%)',
                            }"
                        >
                            <mat-icon class="entity-icon">business</mat-icon>
                        </div>
                        <div class="identity-text">
                            <span class="entity-name">{{ position.name | titlecase }}</span>
                            <span class="entity-subtitle" *ngIf="position.parentName">
                                <mat-icon class="subtitle-icon">subdirectory_arrow_right</mat-icon>
                                {{ position.parentName }}
                            </span>
                            <span class="entity-subtitle" *ngIf="!position.parentName && position.partType">
                                {{ position.partType | titlecase }}
                            </span>
                        </div>
                    </div>
                    <div class="card-footer">
                        <div class="badges-row">
                            <span
                                class="badge"
                                matTooltip="Child Org Parts"
                                [ngStyle]="
                                    position.childCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('positions'),
                                              color: entityColors.getColor('positions'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">account_tree</mat-icon>
                                {{ position.childCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Hats"
                                [ngStyle]="
                                    position.hatCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('hats'),
                                              color: entityColors.getColor('hats'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">school</mat-icon>
                                {{ position.hatCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Staff"
                                [ngStyle]="
                                    position.userCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('users'),
                                              color: entityColors.getColor('users'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">person</mat-icon>
                                {{ position.userCount }}
                            </span>
                            <span class="badge depth-badge" *ngIf="position.depth > 0" matTooltip="Depth level">
                                L{{ position.depth }}
                            </span>
                        </div>
                        <div class="card-actions">
                            <button
                                mat-icon-button
                                matTooltip="View Details"
                                color="primary"
                                (click)="onViewPosition(position); $event.stopPropagation()"
                            >
                                <mat-icon>visibility</mat-icon>
                            </button>
                            <button
                                mat-icon-button
                                matTooltip="Edit Org Part"
                                color="accent"
                                (click)="onEditPosition(position); $event.stopPropagation()"
                            >
                                <mat-icon>edit</mat-icon>
                            </button>
                        </div>
                    </div>
                </mat-card>
            </ng-template>

            <!-- Grouped by parent position view -->
            <ng-container *ngIf="groupByPosition">
                <div *ngFor="let group of positionGroups" class="position-group">
                    <div
                        class="group-header"
                        [ngStyle]="{ 'border-left-color': entityColors.getColor('positions') }"
                        (click)="togglePositionCollapse(group.position)"
                    >
                        <mat-icon class="collapse-icon" [class.collapsed]="collapsedPositions.has(group.position)"
                            >expand_more</mat-icon
                        >
                        <mat-icon [ngStyle]="{ color: entityColors.getColor('positions') }">business</mat-icon>
                        <span class="group-name">{{ group.position }}</span>
                        <span class="group-count">({{ group.positions.length }})</span>
                    </div>
                    <div class="positions-grid" *ngIf="!collapsedPositions.has(group.position)">
                        <ng-container *ngFor="let pos of group.positions">
                            <ng-container
                                *ngTemplateOutlet="positionCardTpl; context: { $implicit: pos }"
                            ></ng-container>
                        </ng-container>
                    </div>
                </div>
                <div *ngIf="positionGroups.length === 0" class="empty-state">
                    <mat-icon class="empty-icon">account_tree</mat-icon>
                    <p>No org part groups to display</p>
                </div>
            </ng-container>

            <!-- Normal (ungrouped) view -->
            <div class="positions-grid" *ngIf="!groupByPosition && filteredPositions.length > 0">
                <ng-container *ngFor="let position of filteredPositions">
                    <ng-container *ngTemplateOutlet="positionCardTpl; context: { $implicit: position }"></ng-container>
                </ng-container>
            </div>

            <!-- Empty State -->
            <div *ngIf="!groupByPosition && filteredPositions.length === 0" class="empty-state">
                <mat-icon class="empty-icon">business</mat-icon>
                <p *ngIf="searchTerm">No org parts found matching "{{ searchTerm }}"</p>
                <p *ngIf="!searchTerm && hasActiveFilter">No org parts match the active filters</p>
                <p *ngIf="!searchTerm && !hasActiveFilter">No org parts in this organization</p>
            </div>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close>Close</button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            .dialog-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin: 0;
                padding: 16px 24px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.12);
            }

            .header-icon {
                color: var(--entity-color-positions, #388e3c);
            }

            .explorer-button {
                color: var(--entity-color-positions, #388e3c);
                margin-left: 8px;
            }

            .explorer-button:hover {
                background-color: rgba(56, 142, 60, 0.1);
            }

            .close-button {
                margin-left: 4px;
            }

            .dialog-content {
                padding: 16px 24px;
                min-height: 400px;
                max-height: 70vh;
                overflow-y: auto;
            }

            .positions-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                gap: 16px;
            }

            .position-card {
                cursor: pointer;
                transition:
                    transform 0.2s,
                    box-shadow 0.2s,
                    border-color 0.2s;
                border: 1px solid rgba(0, 0, 0, 0.08);
                display: flex;
                flex-direction: column;
                padding: 12px 16px;
                background-color: #f5f5f5;
            }

            .position-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                border-color: rgba(0, 0, 0, 0.25);
            }

            .position-card.post-card {
                background-color: rgba(33, 150, 243, 0.08);
                border-color: rgba(33, 150, 243, 0.2);
            }

            .position-card.post-card:hover {
                border-color: rgba(33, 150, 243, 0.4);
            }

            .card-identity {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 12px;
            }

            .entity-avatar {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
            }

            .entity-icon {
                color: white;
                font-size: 22px;
                width: 22px;
                height: 22px;
            }

            .identity-text {
                display: flex;
                flex-direction: column;
                overflow: hidden;
                min-width: 0;
                flex: 1;
            }

            .entity-name {
                font-size: 15px;
                font-weight: 600;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .entity-subtitle {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.55);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                display: flex;
                align-items: center;
                gap: 2px;
            }

            .subtitle-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
                flex-shrink: 0;
            }

            .card-footer {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                border-top: 1px solid rgba(0, 0, 0, 0.06);
                padding-top: 8px;
            }

            .badges-row {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
                min-width: 0;
            }

            .card-actions {
                display: flex;
                flex-shrink: 0;
                align-items: center;
            }

            .card-actions ::ng-deep .mat-mdc-icon-button {
                width: 32px;
                height: 32px;
                padding: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
            }

            .card-actions ::ng-deep .mat-mdc-icon-button .mat-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
            }

            .badge {
                display: inline-flex;
                align-items: center;
                gap: 2px;
                padding: 2px 6px;
                border-radius: 12px;
                font-size: 12px;
                background-color: rgba(0, 0, 0, 0.06);
                color: rgba(0, 0, 0, 0.5);
            }

            .badge-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
            }

            .depth-badge {
                background: color-mix(in srgb, var(--entity-color-positions, #388e3c) 20%, transparent);
                color: var(--entity-color-positions, #388e3c);
                font-size: 11px;
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 48px;
                color: rgba(0, 0, 0, 0.54);
            }

            .empty-icon {
                font-size: 64px;
                height: 64px;
                width: 64px;
                margin-bottom: 16px;
            }

            .mr-2 {
                margin-right: 8px;
            }

            /* Dark theme overrides */
            :host-context(.dark-theme) .position-card {
                background-color: #383838;
                border-color: rgba(255, 255, 255, 0.1);
            }
            :host-context(.dark-theme) .position-card:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
                border-color: rgba(255, 255, 255, 0.2);
            }
            :host-context(.dark-theme) .position-card.post-card {
                background-color: rgba(33, 150, 243, 0.15);
                border-color: rgba(33, 150, 243, 0.3);
            }
            :host-context(.dark-theme) .position-card.post-card:hover {
                border-color: rgba(33, 150, 243, 0.5);
            }
            :host-context(.dark-theme) .entity-subtitle {
                color: rgba(255, 255, 255, 0.55);
            }
            :host-context(.dark-theme) .card-footer {
                border-top-color: rgba(255, 255, 255, 0.08);
            }
            :host-context(.dark-theme) .badge {
                background-color: rgba(255, 255, 255, 0.1);
                color: rgba(255, 255, 255, 0.5);
            }
            :host-context(.dark-theme) .empty-state {
                color: rgba(255, 255, 255, 0.54);
            }

            /* Position group layout */
            .position-group {
                margin-bottom: 24px;
            }

            .group-header {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                margin-bottom: 12px;
                border-left: 3px solid;
                background: rgba(0, 0, 0, 0.03);
                border-radius: 0 4px 4px 0;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                user-select: none;
            }

            .group-header:hover {
                background: rgba(0, 0, 0, 0.06);
            }

            .collapse-icon {
                transition: transform 0.2s;
                color: rgba(0, 0, 0, 0.4);
                font-size: 20px;
                width: 20px;
                height: 20px;
            }

            .collapse-icon.collapsed {
                transform: rotate(-90deg);
            }

            .group-name {
                font-weight: 600;
            }

            .group-count {
                color: rgba(0, 0, 0, 0.45);
                font-weight: 400;
            }

            :host-context(.dark-theme) .group-header {
                background: rgba(255, 255, 255, 0.04);
            }
            :host-context(.dark-theme) .group-header:hover {
                background: rgba(255, 255, 255, 0.08);
            }
            :host-context(.dark-theme) .collapse-icon {
                color: rgba(255, 255, 255, 0.4);
            }
            :host-context(.dark-theme) .group-count {
                color: rgba(255, 255, 255, 0.45);
            }

            /* Phones: full-screen dialog (global .ob-list-dialog) → tighten chrome and
           drop the 280px-min grid to a single full-width column. */
            @media (max-width: 767.98px) {
                .dialog-header {
                    padding: 12px 16px;
                    font-size: 18px;
                }
                .dialog-content {
                    padding: 12px 16px;
                    min-height: 0;
                }
                /* minmax(0,1fr) (not 1fr) removes the implicit min-content floor so the
               card actually shrinks to the viewport instead of overflowing right. */
                .positions-grid {
                    grid-template-columns: minmax(0, 1fr);
                    gap: 12px;
                }
                .positions-grid > * {
                    min-width: 0;
                    max-width: 100%;
                }
                .card-identity,
                .identity-text {
                    min-width: 0;
                }
                /* Badges take a full row (wrapping internally), actions drop below. */
                .card-footer {
                    flex-wrap: wrap;
                }
                .badges-row {
                    flex: 1 1 100%;
                }
                .card-actions {
                    margin-left: auto;
                }
            }
        `,
    ],
})
export class OrgPositionsListDialogComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    private obService = inject(OrgBoardService);
    public entityColors = inject(OrgEntityColorsService);
    public listView = inject(OrgListViewService);
    allPositions: PositionDisplayCard[] = [];
    filteredPositions: PositionDisplayCard[] = [];
    searchTerm = '';

    // Filter configs for shared filter bar
    filterConfig: OrgFilterConfig[] = [];
    rightFilterConfig: OrgFilterConfig[] = [];

    // Filters (kept for internal filterPositions logic)
    activeTypeFilters = new Set<string>();
    noHatsFilter = false;
    noSgFilter = false;
    noUsersFilter = false;
    noChildrenFilter = false;
    groupByPosition = false;
    positionGroups: { position: string; positions: PositionDisplayCard[] }[] = [];
    collapsedPositions = new Set<string>();
    executiveCount = 0;
    divisionCount = 0;
    departmentCount = 0;
    sectionCount = 0;
    unitCount = 0;
    postCount = 0;
    noHatsCount = 0;
    noSgCount = 0;
    noUsersCount = 0;
    noChildrenCount = 0;

    constructor(
        public dialogRef: MatDialogRef<OrgPositionsListDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PositionsListDialogData,
        private dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.loadPositions();
        this.subscribeToOrgUpdates();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private subscribeToOrgUpdates(): void {
        this.obService.organization$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
            if (!org) return;
            this.data.organization = org;
            this.loadPositions();
        });
    }

    private loadPositions(): void {
        const org = this.data.organization;
        const orgParts = org?.orgParts || [];
        const orgHats = org?.orgHats;

        // Build parent map for quick lookup
        const partMap = new Map<string, OrgPart>();
        for (const part of orgParts) {
            const key = part.partKey || part.key || '';
            if (key) partMap.set(key, part);
        }

        // Count children for each position
        const childCounts = new Map<string, number>();
        for (const part of orgParts) {
            if (part.parentPartKey) {
                const count = childCounts.get(part.parentPartKey) || 0;
                childCounts.set(part.parentPartKey, count + 1);
            }
        }

        // Count hats and users per position, and collect hat/user names for search
        const hatCounts = new Map<string, number>();
        const userCounts = new Map<string, number>();
        const hatNamesByPos = new Map<string, Set<string>>();
        const userNamesByPos = new Map<string, Set<string>>();

        const sgCounts = new Map<string, number>();

        if (orgHats?.hatsKeys) {
            for (const hatKey of orgHats.hatsKeys) {
                const hat = orgHats[hatKey] as OrgHat;
                if (!hat) continue;

                const positionArray = hat.positionArray || [];
                const users = hat.assignedToArray || hat.userKeys || [];
                const hatRecord = hat as unknown as Record<string, unknown>;
                const sgArr = hatRecord['securityGroupsArray'];
                const hatSgCount = Array.isArray(sgArr) ? sgArr.length : 0;

                for (const posKey of positionArray) {
                    hatCounts.set(posKey, (hatCounts.get(posKey) || 0) + 1);
                    userCounts.set(posKey, (userCounts.get(posKey) || 0) + users.length);
                    sgCounts.set(posKey, (sgCounts.get(posKey) || 0) + hatSgCount);

                    // Collect hat names
                    if (!hatNamesByPos.has(posKey)) hatNamesByPos.set(posKey, new Set());
                    if (hat.name) hatNamesByPos.get(posKey)!.add(hat.name);

                    // Collect user names
                    if (!userNamesByPos.has(posKey)) userNamesByPos.set(posKey, new Set());
                    const orgUsers = org?.orgUsers;
                    if (orgUsers) {
                        for (const uid of users) {
                            const userName = orgUsers[uid]?.name || orgUsers[uid]?.userName;
                            if (userName) userNamesByPos.get(posKey)!.add(userName);
                        }
                    }
                }
            }
        }

        this.allPositions = restrictToKeys(orgParts, this.data.onlyKeys, (p) => p.partKey || p.key || '').map(
            (part) => {
                const key = part.partKey || part.key || '';
                const parentPart = part.parentPartKey ? partMap.get(part.parentPartKey) : null;

                return {
                    key,
                    name: part.name || part.mainTitle || key,
                    partType: part.partType || '',
                    resolvedType: resolvePartType(part),
                    depth: part.depth || 0,
                    parentName: parentPart?.name || parentPart?.mainTitle || '',
                    path: part.path || '',
                    hatCount: hatCounts.get(key) || 0,
                    userCount: userCounts.get(key) || 0,
                    childCount: childCounts.get(key) || 0,
                    sgCount: sgCounts.get(key) || 0,
                    hatNames: Array.from(hatNamesByPos.get(key) || []),
                    userNames: Array.from(userNamesByPos.get(key) || []),
                    colour: part.colour || { background: '#4fc3f7', color: '#fff' },
                    orgPart: part,
                };
            }
        );

        // Sort by depth then name
        this.allPositions.sort((a, b) => {
            if (a.depth !== b.depth) return a.depth - b.depth;
            return a.name.localeCompare(b.name);
        });

        this.computeFilterCounts();
        this.filteredPositions = [...this.allPositions];
    }

    private computeFilterCounts(): void {
        this.executiveCount = this.allPositions.filter((p) => p.resolvedType === 'executive').length;
        this.divisionCount = this.allPositions.filter((p) => p.resolvedType === 'division').length;
        this.departmentCount = this.allPositions.filter((p) => p.resolvedType === 'department').length;
        this.sectionCount = this.allPositions.filter((p) => p.resolvedType === 'section').length;
        this.unitCount = this.allPositions.filter((p) => p.resolvedType === 'unit').length;
        this.postCount = this.allPositions.filter((p) => p.resolvedType === 'post').length;
        this.noHatsCount = this.allPositions.filter((p) => p.hatCount === 0).length;
        this.noSgCount = this.allPositions.filter((p) => p.sgCount === 0).length;
        this.noUsersCount = this.allPositions.filter((p) => p.userCount === 0).length;
        this.noChildrenCount = this.allPositions.filter((p) => p.childCount === 0).length;
        this.buildFilterConfigs();
    }

    private buildFilterConfigs(): void {
        this.filterConfig = [
            { key: 'executive', label: 'Executives', icon: 'star', count: this.executiveCount },
            { key: 'division', label: 'Divisions', icon: 'domain', count: this.divisionCount },
            { key: 'department', label: 'Departments', icon: 'apartment', count: this.departmentCount },
            { key: 'section', label: 'Sections', icon: 'layers', count: this.sectionCount },
            { key: 'unit', label: 'Units', icon: 'view_module', count: this.unitCount },
            { key: 'post', label: 'Posts', icon: 'badge', count: this.postCount },
            { key: 'noChildren', label: 'Leaf', icon: 'park', count: this.noChildrenCount },
            { key: 'noHats', label: 'No Hats', icon: 'school', count: this.noHatsCount },
            { key: 'noSg', label: 'No SGs', icon: 'shield', count: this.noSgCount },
            { key: 'noUsers', label: 'No Staff', icon: 'person_off', count: this.noUsersCount },
        ];
        this.rightFilterConfig = [{ key: 'groupByPosition', label: 'By Org Part', icon: 'account_tree' }];
    }

    get hasActiveFilter(): boolean {
        return (
            this.activeTypeFilters.size > 0 ||
            this.noHatsFilter ||
            this.noSgFilter ||
            this.noUsersFilter ||
            this.noChildrenFilter
        );
    }

    public onSearchChanged(term: string): void {
        this.searchTerm = term;
        this.filterPositions();
    }

    public onFiltersChanged(activeFilters: Set<string>): void {
        // Map filter keys to internal state
        this.activeTypeFilters.clear();
        const typeKeys = ['executive', 'division', 'department', 'section', 'unit', 'post'];
        for (const key of typeKeys) {
            if (activeFilters.has(key)) {
                this.activeTypeFilters.add(key);
            }
        }
        this.noChildrenFilter = activeFilters.has('noChildren');
        this.noHatsFilter = activeFilters.has('noHats');
        this.noSgFilter = activeFilters.has('noSg');
        this.noUsersFilter = activeFilters.has('noUsers');

        // Handle groupByPosition toggle
        const newGroupByPosition = activeFilters.has('groupByPosition');
        if (newGroupByPosition !== this.groupByPosition) {
            this.groupByPosition = newGroupByPosition;
            if (!this.groupByPosition) {
                this.collapsedPositions.clear();
            }
        }

        this.filterPositions();
    }

    filterPositions(): void {
        let result = [...this.allPositions];

        // Text search across name, type, parent, hats, users (always applied as AND)
        const term = this.searchTerm.toLowerCase().trim();
        if (term) {
            result = result.filter(
                (pos) =>
                    fuzzyMatch(pos.name.toLowerCase(), term) ||
                    fuzzyMatch(pos.resolvedType, term) ||
                    fuzzyMatch(pos.parentName.toLowerCase(), term) ||
                    pos.hatNames.some((h) => fuzzyMatch(h.toLowerCase(), term)) ||
                    pos.userNames.some((u) => fuzzyMatch(u.toLowerCase(), term))
            );
        }

        // All remaining filters are inclusive (OR/union)
        if (this.hasActiveFilter) {
            result = result.filter((p) => {
                // Type filters: match if resolvedType is in the active set
                if (this.activeTypeFilters.size > 0) {
                    if (this.activeTypeFilters.has(p.resolvedType)) return true;
                }
                // Quality filters
                if (this.noChildrenFilter && p.childCount === 0) return true;
                if (this.noHatsFilter && p.hatCount === 0) return true;
                if (this.noSgFilter && p.sgCount === 0) return true;
                if (this.noUsersFilter && p.userCount === 0) return true;
                return false;
            });
        }

        this.filteredPositions = result;

        if (this.groupByPosition) {
            this.buildPositionGroups();
        }
    }

    togglePositionCollapse(position: string): void {
        if (this.collapsedPositions.has(position)) {
            this.collapsedPositions.delete(position);
        } else {
            this.collapsedPositions.add(position);
        }
    }

    private buildPositionGroups(): void {
        const groupMap = new Map<string, PositionDisplayCard[]>();

        this.filteredPositions.forEach((pos) => {
            const parentName = pos.parentName || 'No Parent';
            if (!groupMap.has(parentName)) groupMap.set(parentName, []);
            groupMap.get(parentName)?.push(pos);
        });

        this.positionGroups = Array.from(groupMap.entries())
            .map(([position, positions]) => ({ position, positions }))
            .sort((a, b) => {
                if (a.position === 'No Parent') return 1;
                if (b.position === 'No Parent') return -1;
                return a.position.localeCompare(b.position);
            });

        // Collapse all by default on first build
        if (this.collapsedPositions.size === 0 && this.positionGroups.length > 0) {
            this.positionGroups.forEach((g) => this.collapsedPositions.add(g.position));
        }
    }

    onPositionDoubleClick(position: PositionDisplayCard): void {
        this.onViewPosition(position);
    }

    onViewPosition(position: PositionDisplayCard): void {
        import('../org-position-details-dialog').then((m) => {
            this.dialog.open(m.OrgPositionDetailsDialogComponent, {
                data: {
                    organization: this.data.organization,
                    position: position.orgPart,
                },
                height: '85%',
                width: '85%',
                maxHeight: 'unset',
                maxWidth: 'unset',
            });
        });
    }

    onEditPosition(position: PositionDisplayCard): void {
        import('../org-position-dialog/org-edit-position-dialog/org-edit-position-dialog.component').then((m) => {
            this.dialog
                .open(m.OrgEditPositionDialogComponent, {
                    data: {
                        part: position.orgPart,
                        partTypes: ['executive', 'division', 'department', 'section', 'unit', 'post'],
                        organization: this.data.organization,
                    },
                    width: '80%',
                    height: '85%',
                    minHeight: '300px',
                    maxWidth: '98vw',
                    panelClass: 'ng-resizable-dialog',
                })
                .afterClosed()
                .subscribe((result) => {
                    if (result) {
                        console.log('Edit position result:', result);
                        // Refresh the list after edit
                        this.loadPositions();
                    }
                });
        });
    }

    openExplorer(): void {
        import('../org-position-explorer-dialog').then((m) => {
            this.dialog.open(m.OrgPositionExplorerDialogComponent, {
                data: { organization: this.data.organization },
                width: '90%',
                height: '90vh',
                maxWidth: '1400px',
                maxHeight: '900px',
                panelClass: ['position-explorer-dialog', 'ob-list-dialog'],
            });
        });
    }
}
